/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { memo } from "react";
import type { CSSProperties, FunctionComponent, JSX } from "react";

import { SkeletonShape } from "./SkeletonProps";
import type { SkeletonProps } from "./SkeletonProps";

import "./Skeleton.scss";

/** Milliseconds each {@link SkeletonProps.sequence} step adds to the pulse. */
const STAGGER_STEP_MS: number = 100;

/**
 * Number of steps before the stagger wraps.
 *
 * Must stay in sync with `$skeleton-stagger-cycle` in styles/_skeleton.scss.
 */
const STAGGER_CYCLE: number = 10;

/**
 * Skeleton — one placeholder shape.
 *
 * The single atom every loading state in the app is built from: the list draws
 * rows of them, the grid draws cells of them, and a screen that needs a
 * card-shaped placeholder composes several. Keeping the fill, the radius and
 * above all the pulse in one place is what makes two different surfaces read as
 * the same product loading, rather than as two unrelated animations.
 *
 * Purely decorative — it is `aria-hidden`, and the region that owns it carries
 * the `aria-busy` that assistive technology actually acts on.
 */
const SkeletonComponent: FunctionComponent<SkeletonProps> = (
  props: SkeletonProps
): JSX.Element => {
  const {
    shape = SkeletonShape.BLOCK,
    width,
    height,
    radius,
    sequence = 0,
    className
  } = props;

  // Only the overrides are emitted; everything else is left to the stylesheet
  // so the shape's defaults stay in one file.
  const style: CSSProperties = {};
  if (width !== undefined) {
    style.width = width;
  }
  if (height !== undefined) {
    style.height = height;
  }
  if (radius !== undefined) {
    style.borderRadius = radius;
  }

  // Inline rather than an `:nth-child` ladder in the stylesheet: the caller
  // knows the position even when the placeholders are not siblings, and a
  // ladder would have to declare an upper bound the grid does not have.
  const stagger: number = Math.max(Math.trunc(sequence), 0) % STAGGER_CYCLE;
  if (stagger > 0) {
    style.animationDelay = stagger * STAGGER_STEP_MS + "ms";
  }

  const rootClassName: string = ["skeleton", "skeleton--" + shape, className]
    .filter(
      (token?: string): token is string => token !== undefined && token !== ""
    )
    .join(" ");

  return <span className={rootClassName} style={style} aria-hidden="true" />;
};

export const Skeleton = memo(SkeletonComponent);
